import { useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type { useConfiguratorData_ConfiguratorDataQuery } from './__generated__/useConfiguratorData_ConfiguratorDataQuery.graphql';
import type { ConfiguratorData } from './types';

const configuratorDataQuery = graphql`
    query useConfiguratorData_ConfiguratorDataQuery($id: ID!) {
        node(id: $id) {
            ... on Model {
                scenes {
                    meshSides {
                        meshID
                        sideID
                    }
                    meshDesignEffects {
                        effectID
                        meshID
                        name
                    }
                }
                attributes {
                    name
                    attributeCode
                    description
                    options {
                        optionID
                        name
                        optionCode
                        description
                        imageUrl
                        constraintExpression {
                            op
                        }
                    }
                    pricings {
                        attributePricingID
                        selectedOptionID
                        price
                    }
                }
            }
        }
    }
`;

export const useConfiguratorData = (productId: string): ConfiguratorData => {
    const data = useLazyLoadQuery<useConfiguratorData_ConfiguratorDataQuery>(configuratorDataQuery, { id: productId }, { fetchPolicy: 'store-or-network' });

    return useMemo(() => {
        const model = data.node;
        const scenes = model?.scenes || [];

        // Build scene meshes from meshSides
        const meshMap = new Map<string, { sideID: number }[]>();
        for (const scene of scenes) {
            for (const ms of scene.meshSides || []) {
                if (!meshMap.has(ms.meshID)) meshMap.set(ms.meshID, []);
                meshMap.get(ms.meshID)!.push({ sideID: ms.sideID });
            }
        }

        // Build materials from meshDesignEffects
        const meshMaterialsMap = new Map<string, { name: string; effectID: string }[]>();
        for (const scene of scenes) {
            for (const effect of scene.meshDesignEffects || []) {
                if (!meshMaterialsMap.has(effect.meshID)) meshMaterialsMap.set(effect.meshID, []);
                meshMaterialsMap.get(effect.meshID)!.push({ name: effect.name || '', effectID: effect.effectID });
            }
        }

        const sceneMeshes = Array.from(meshMap.entries()).map(([meshID, sides]) => ({
            meshID,
            name: meshID,
            sides,
            materials: meshMaterialsMap.get(meshID) || []
        }));

        // Build attributes
        const attributes = (model?.attributes || []).map((a) => {
            const pricings = a.pricings || [];
            const priceByOptionId = new Map<number, number>();
            pricings.forEach((p) => {
                if (p && p.selectedOptionID != null) priceByOptionId.set(p.selectedOptionID, p.price);
            });
            return {
                name: a.name || 'Unnamed',
                code: a.attributeCode || '',
                description: a.description || '',
                options: (a.options || [])
                    .filter((o): o is NonNullable<typeof o> => o != null)
                    .map((o) => ({
                        optionID: o.optionID ?? null,
                        name: o.name || 'Unnamed',
                        optionCode: o.optionCode || '',
                        description: o.description || '',
                        imageUrl: o.imageUrl || null,
                        hasLink: !!o.constraintExpression,
                        price: o.optionID != null ? (priceByOptionId.get(o.optionID) ?? null) : null
                    }))
            };
        });

        return { sceneMeshes, attributes };
    }, [data]);
};
